// Media/upload helpers for the SPA (decomposition). Browser-only (canvas,
// FileReader, window). openSignedDoc uses the API client.
import { api } from './api.js';

// Open a knowledge-doc attachment (or any signed-URL resource) in a new
// tab. Asks the server to mint a 5-minute HMAC-signed URL for the resource,
// then opens it. Replaces the legacy `?u=<id>` query-string pattern (audit
// C5) — browsers can't carry auth headers on a plain <a href>, so the
// server signs the URL itself. `headers` is the same auth shape the rest of
// the SPA uses; pass `{ 'x-user-id': String(currentUser.id) }`.
export async function openSignedDoc(docId, filename, headers) {
  try {
    const r = await api('POST', '/api/uploads/sign',
      { kind: 'doc', id: docId, filename }, headers);
    if (!r?.signed_url) throw new Error('No signed URL returned');
    window.open(r.signed_url, '_blank', 'noopener,noreferrer');
  } catch (e) {
    alert('Could not open document: ' + (e?.message || 'unknown error'));
  }
}

// Downscale a phone photo before upload. Modern iPhones produce 5–15 MB
// JPEGs that blow the 8 MB server cap on chat / dispute / equipment uploads.
// Drawing into a max-2048px canvas and re-encoding at quality 0.85 typically
// lands at 600 KB–1.5 MB with no visible loss. Returns { base64, mime,
// filename } ready for /api/chat/uploads or any JSON+base64 ingest. Falls
// back to raw bytes if canvas decode fails (HEIC etc).
export async function imagePreflight(file, { maxEdge = 2048, quality = 0.85 } = {}) {
  if (!file || !file.type?.startsWith('image/')) {
    throw new Error('Please pick an image.');
  }
  if (file.size > 25 * 1024 * 1024) {
    throw new Error('That image is huge — please pick something under 25 MB.');
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
  let base64, mime, filename;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Could not decode image'));
      i.src = dataUrl;
    });
    const longest = Math.max(img.width, img.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL('image/jpeg', quality);
    const i = out.indexOf(',');
    base64 = i >= 0 ? out.slice(i + 1) : out;
    mime = 'image/jpeg';
    filename = (file.name || 'photo').replace(/\.(heic|heif|png|webp)$/i, '.jpg');
  } catch (e) {
    // Fallback: raw bytes (canvas couldn't decode the source).
    const i = dataUrl.indexOf(',');
    base64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
    mime = file.type;
    filename = file.name || 'photo';
  }
  return { base64, mime, filename };
}
